import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * 統一テキスト入力処理フック
 * 全ての入力フィールドで一貫したIME処理、フォーカス管理、状態更新を提供
 */

interface UseTextInputOptions {
  initialValue?: string;
  onValueChange?: (value: string) => void;
  debounceMs?: number;
  validateInput?: (value: string) => boolean;
  autoComplete?: string;
  spellCheck?: boolean;
}

interface UseTextInputReturn {
  value: string;
  setValue: (value: string) => void;
  isComposing: boolean;
  inputProps: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onCompositionStart: () => void;
    onCompositionEnd: (e: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    autoComplete: string;
    spellCheck: boolean;
  };
  // デバッグ・監視用
  lastUpdateTime: number;
  changeCount: number;
}

export const useTextInput = ({
  initialValue = '',
  onValueChange,
  debounceMs = 0,
  validateInput,
  autoComplete = 'off',
  spellCheck = false,
}: UseTextInputOptions = {}): UseTextInputReturn => {
  // 内部状態
  const [value, setValue] = useState<string>(initialValue);
  const [isComposing, setIsComposing] = useState<boolean>(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(Date.now());
  const [changeCount, setChangeCount] = useState<number>(0);

  // Refs for stable references
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const compositionValueRef = useRef<string>('');
  const lastChangeTimeRef = useRef<number>(Date.now());

  // デバウンス付きの外部通知
  const notifyValueChange = useCallback(
    (newValue: string) => {
      if (!onValueChange) return;

      if (debounceMs > 0) {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
          onValueChange(newValue);
        }, debounceMs);
      } else {
        onValueChange(newValue);
      }
    },
    [onValueChange, debounceMs]
  );

  // 値更新の統一処理
  const updateValue = useCallback(
    (newValue: string, fromComposition = false) => {
      // バリデーション
      if (validateInput && !validateInput(newValue)) {
        return;
      }

      // 重複更新の防止
      if (newValue === value && !fromComposition) {
        return;
      }

      // 状態更新
      setValue(newValue);
      setLastUpdateTime(Date.now());
      setChangeCount(prev => prev + 1);
      lastChangeTimeRef.current = Date.now();

      // 外部通知
      notifyValueChange(newValue);
    },
    [value, validateInput, notifyValueChange]
  );

  // IME Composition 開始
  const handleCompositionStart = useCallback(() => {
    setIsComposing(true);
    compositionValueRef.current = value;
  }, [value]);

  // IME Composition 終了
  const handleCompositionEnd = useCallback(
    (e: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsComposing(false);

      // Composition終了時の値を取得
      const compositionValue = e.currentTarget.value;
      compositionValueRef.current = '';

      // 値を更新（fromComposition = true）
      updateValue(compositionValue, true);
    },
    [updateValue]
  );

  // 通常の入力変更処理
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;

      // IME入力中は内部状態のみ更新（外部通知なし）
      if (isComposing) {
        setValue(newValue);
        return;
      }

      // 通常の入力変更処理
      updateValue(newValue);
    },
    [isComposing, updateValue]
  );

  // 外部からの値設定（プログラマティック）
  const setValueProgrammatically = useCallback((newValue: string) => {
    setValue(newValue);
    setLastUpdateTime(Date.now());
    // 外部からの設定時は通知しない（無限ループ防止）
  }, []);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // 初期値の同期 - valueの依存関係を除去して循環更新を防止
  useEffect(() => {
    if (initialValue !== value && !isComposing) {
      setValue(initialValue);
    }
  }, [initialValue, isComposing]); // valueを依存関係から除去

  return {
    value,
    setValue: setValueProgrammatically,
    isComposing,
    inputProps: {
      value,
      onChange: handleChange,
      onCompositionStart: handleCompositionStart,
      onCompositionEnd: handleCompositionEnd,
      autoComplete,
      spellCheck,
    },
    lastUpdateTime,
    changeCount,
  };
};

export default useTextInput;
